import { useGamesStore } from '@/entities/games/model/useGamesStore';
import { usePlayers } from '@/entities/players/model/usePlayers';
import { useProfile } from '@/entities/profile/model/useProfile';
import { useStatistics } from '@/entities/statistics/model/useStatistics';
import { useAdmin } from '@/entities/admin/model/useAdmin';
// import { useStatisticsFilters } from '@/entities/statistics/model/useStatisticsFilters';
import { useTournamentList } from '@/entities/tournaments/model/useTournamentList';
import React, { ReactNode, createContext, useContext } from 'react';

type Store = {
    players: ReturnType<typeof usePlayers> | null;
    tournaments: ReturnType<typeof useTournamentList> | null;
    player: ReturnType<typeof useProfile> | null;
    statistics: ReturnType<typeof useStatistics> | null;
    games: ReturnType<typeof useGamesStore> | null;
    admin: ReturnType<typeof useAdmin> | null;
};

const Context = createContext<Store>({
    players: null,
    tournaments: null,
    player: null,
    statistics: null,
    games: null,
    admin: null,
});

export const StoreProvider = ({ children }: { children: ReactNode }) => {
    const players = usePlayers();
    const tournaments = useTournamentList();
    const player = useProfile();
    const statistics = useStatistics();
    const games = useGamesStore();
    const admin = useAdmin();
    return (
        <Context.Provider value={{ players, tournaments, player, statistics, games, admin }}>
            {children}
        </Context.Provider>
    );
};

export const usePlayersContext = () => {
    const { players } = useContext(Context);
    if (players === undefined) {
        throw new Error('usePlayersContext must be used within a StoreProvider');
    }
    return players;
};

export const useTournamentsContext = () => {
    const { tournaments } = useContext(Context);
    if (tournaments === undefined) {
        throw new Error('useTournamentsContext must be used within a StoreProvider');
    }
    return tournaments;
};

export const usePlayerContext = () => {
    const { player } = useContext(Context);
    if (player === undefined) {
        throw new Error('usePlayerContext must be used within a StoreProvider');
    }
    return player;
};

export const useStatisticsContext = () => {
    const { statistics } = useContext(Context);
    if (statistics === undefined) {
        throw new Error('useStatisticsContext must be used within a StoreProvider');
    }
    return statistics;
};

export const useGamesContext = () => {
    const { games } = useContext(Context);
    if (games === undefined) {
        throw new Error('useGamesContext must be used within a StoreProvider');
    }
    return games;
};

export const useAdminContext = () => {
    const { admin } = useContext(Context);
    if (admin === undefined) {
        throw new Error('useAdminContext must be used within a StoreProvider');
    }
    return admin;
};
